import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { payrollAPI } from '../../services/api';
import { statuteLabel } from './plainLabels';
import { FieldGrid, PageHeader, Section } from './payrollUi';

const EMPLOYEE_LINES = [
  'Basic pay',
  'Dearness allowance',
  'Retaining allowance',
  'House rent allowance',
  'Conveyance allowance',
  'Special allowance',
];
const CONTRACTOR_LINES = ['Professional fee'];

const dateField = { type: 'date', InputLabelProps: { shrink: true }, fullWidth: true };

export default function PaySetupPage() {
  const qc = useQueryClient();
  const setup = useQuery(['pay-setup'], async () => (await payrollAPI.getSetup()).data.data);
  const [tab, setTab] = useState(0);
  const onOk = (message) => ({
    onSuccess: () => {
      qc.invalidateQueries(['pay-setup']);
      toast.success(message);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Request failed'),
  });
  const saveTemplate = useMutation((body) => payrollAPI.saveTemplate(body), onOk('Template saved'));
  const saveRate = useMutation((body) => payrollAPI.saveIncentiveRate(body), onOk('Rate saved'));
  const saveCategory = useMutation((body) => payrollAPI.saveIncentiveCategory(body), onOk('Category saved'));
  const saveRule = useMutation((body) => payrollAPI.saveRule(body), onOk('Rule saved'));
  const saveReview = useMutation(({ key, nextReviewOn }) => payrollAPI.setReviewDate(key, { nextReviewOn }), onOk('Review date saved'));

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Pay setup"
        subtitle="Make a salary template here. On Staff profiles, pick that template and type the person’s total."
      />
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
        <Tab label="Salary templates" />
        <Tab label="Incentives" />
        <Tab label="Employer" />
        <Tab label="Tax and contributions" />
      </Tabs>
      {tab === 0 && (
        <TemplateStudio
          templates={setup.data?.templates || []}
          saving={saveTemplate.isLoading}
          onSave={(body) => saveTemplate.mutate(body)}
          onDelete={(id) => payrollAPI.deleteTemplate(id).then(() => {
            toast.success('Template removed');
            qc.invalidateQueries(['pay-setup']);
          }).catch((e) => toast.error(e.response?.data?.message || 'Request failed'))}
        />
      )}
      {tab === 1 && (
        <Incentives
          categories={setup.data?.categories || []}
          rates={setup.data?.rates || []}
          onSaveRate={(body) => saveRate.mutate(body)}
          onSaveCategory={(body) => saveCategory.mutate(body)}
        />
      )}
      {tab === 2 && (
        <EmployerAndBasis
          statutes={setup.data?.statutes || []}
          employerRow={setup.data?.employer}
          onChanged={() => qc.invalidateQueries(['pay-setup'])}
        />
      )}
      {tab === 3 && (
        <Statutory
          rules={setup.data?.statutoryRules || []}
          reviews={setup.data?.reviews || []}
          onSaveRule={(body) => saveRule.mutate(body)}
          onSaveReview={(key, nextReviewOn) => saveReview.mutate({ key, nextReviewOn })}
        />
      )}
    </Stack>
  );
}

function rupee(value) {
  return `₹${Math.floor(Number(value) + 0.5).toLocaleString('en-IN')}`;
}

function suggestedLines(payeeType) {
  if (payeeType === 'contractor') {
    return [{ lineName: 'Professional fee', mode: 'percent', percentage: '100', fixedAmount: '', takesRest: true }];
  }
  return [
    { lineName: 'Basic pay', mode: 'percent', percentage: '50', fixedAmount: '', takesRest: false },
    { lineName: 'Special allowance', mode: 'percent', percentage: '', fixedAmount: '', takesRest: true },
  ];
}

const RUPEE_LINES = new Set(['House rent allowance', 'Conveyance allowance']);

function insertBeforeRest(lines, line) {
  const restAt = lines.findIndex((row) => row.takesRest);
  if (restAt < 0) return [...lines, line];
  return [...lines.slice(0, restAt), line, ...lines.slice(restAt)];
}

function shareAmount(line, total) {
  if (line.takesRest) return null;
  if (line.mode === 'rupees') return Number(line.fixedAmount || 0);
  return Math.floor((total * Number(line.percentage || 0)) / 100 + 0.5);
}

function TemplateStudio({ templates, saving, onSave, onDelete }) {
  const [draft, setDraft] = useState({ id: null, name: '', payeeType: 'employee', lines: suggestedLines('employee') });
  const [customName, setCustomName] = useState('');
  const [examplePay, setExamplePay] = useState('10000');
  const catalog = draft.payeeType === 'contractor' ? CONTRACTOR_LINES : EMPLOYEE_LINES;
  const chosen = new Set(draft.lines.map((line) => line.lineName));
  const lines = draft.lines;
  const restLine = lines.find((line) => line.takesRest);
  const missingValue = lines.some((line) => !line.takesRest && (line.mode === 'rupees' ? line.fixedAmount === '' : line.percentage === ''));
  const percentSum = lines.reduce((n, line) => (line.takesRest || line.mode === 'rupees' ? n : n + Number(line.percentage || 0)), 0);
  const example = Number(examplePay);
  const exampleReady = Number.isFinite(example) && example > 0;
  const usedOnExample = exampleReady
    ? lines.reduce((n, line) => (line.takesRest ? n : n + shareAmount(line, example)), 0)
    : 0;
  const restOnExample = exampleReady ? example - usedOnExample : null;
  const hasRupees = lines.some((line) => line.mode === 'rupees' && !line.takesRest);
  const percentsFit = percentSum <= 100.001 && (restLine || Math.abs(percentSum - 100) < 0.001);
  const ready = Boolean(draft.name.trim()) && lines.length > 0 && !missingValue && percentsFit && (!hasRupees || restLine) && (restOnExample == null || restOnExample >= -0.001);
  const basic = lines.find((line) => line.lineName === 'Basic pay');
  const excludedOnExample = exampleReady
    ? lines.filter((line) => line.lineName === 'House rent allowance' || line.lineName === 'Conveyance allowance').reduce((n, line) => n + shareAmount(line, example), 0)
    : 0;

  const setLines = (next) => setDraft({ ...draft, lines: next });
  const addLine = (lineName) => {
    if (!lineName || chosen.has(lineName)) return;
    setLines(insertBeforeRest(draft.lines, {
      lineName,
      mode: RUPEE_LINES.has(lineName) ? 'rupees' : 'percent',
      percentage: '',
      fixedAmount: '',
      takesRest: false,
    }));
  };
  const makeRest = (lineName) => {
    setLines(draft.lines.map((line) => ({ ...line, takesRest: line.lineName === lineName })));
  };

  const load = (template) => {
    const next = (template.lines || []).map((line) => {
      const takesRest = Boolean(line.takes_rest);
      const rupees = line.fixed_amount != null && line.fixed_amount !== '';
      return {
        lineName: line.line_name,
        mode: rupees ? 'rupees' : 'percent',
        percentage: takesRest || rupees ? '' : String(line.percentage),
        fixedAmount: rupees ? String(line.fixed_amount) : '',
        takesRest,
      };
    });
    setDraft({
      id: template.id,
      name: template.name,
      payeeType: template.payee_type,
      lines: next,
    });
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2, alignItems: 'start' }}>
      <Section title="Saved templates">
        <Button fullWidth sx={{ mb: 1 }} onClick={() => setDraft({ id: null, name: '', payeeType: 'employee', lines: suggestedLines('employee') })}>New template</Button>
        <List dense sx={{ mx: -1 }}>
          {templates.map((template) => (
            <ListItemButton key={template.id} selected={template.id === draft.id} onClick={() => load(template)}>
              <ListItemText
                primary={template.name}
                secondary={template.payee_type === 'contractor' ? 'Contractor' : 'Employee'}
              />
            </ListItemButton>
          ))}
          {templates.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>None saved yet.</Typography>}
        </List>
      </Section>
      <Section
        title={draft.id ? draft.name || 'Edit template' : 'New template'}
        action={(
          <Stack direction="row" spacing={1}>
            {draft.id && <Button color="inherit" onClick={() => onDelete(draft.id)}>Remove</Button>}
            <Button variant="contained" disabled={!ready || saving} onClick={() => onSave({
              id: draft.id,
              name: draft.name.trim(),
              payeeType: draft.payeeType,
              lines: lines.map((line) => ({
                lineName: line.lineName,
                takesRest: Boolean(line.takesRest),
                percentage: line.mode === 'rupees' || line.takesRest ? 0 : Number(line.percentage),
                fixedAmount: line.mode === 'rupees' && !line.takesRest ? Number(line.fixedAmount) : null,
              })),
            })}>Save template</Button>
          </Stack>
        )}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Name it. Basic pay starts at 50%. A part can be a percent or a fixed amount, such as house rent. Special allowance takes whatever is left.
        </Typography>
        <FieldGrid>
          <TextField label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Teachers" fullWidth />
          <TextField
            select
            label="Who"
            value={draft.payeeType}
            onChange={(e) => setDraft({ ...draft, payeeType: e.target.value, lines: suggestedLines(e.target.value) })}
            fullWidth
          >
            <MenuItem value="employee">Employees</MenuItem>
            <MenuItem value="contractor">Contractors</MenuItem>
          </TextField>
        </FieldGrid>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
          <Button onClick={() => setLines(suggestedLines(draft.payeeType))}>Use suggested split</Button>
          <TextField
            size="small"
            label="Try it on a salary of"
            type="number"
            value={examplePay}
            onChange={(e) => setExamplePay(e.target.value)}
            sx={{ width: { sm: 220 } }}
          />
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {lines.map((line) => {
            const share = line.takesRest ? restOnExample : (exampleReady ? shareAmount(line, example) : null);
            return (
              <Stack key={line.lineName} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography>{line.lineName}</Typography>
                  {line.takesRest && <Typography variant="body2" color="text.secondary">Takes whatever is left</Typography>}
                </Box>
                {line.takesRest ? (
                  <Typography sx={{ minWidth: 120 }}>{share == null ? 'Depends on the salary' : rupee(share)}</Typography>
                ) : (
                  <>
                    <TextField
                      select
                      size="small"
                      label="How"
                      value={line.mode}
                      onChange={(e) => setLines(draft.lines.map((row) => (row.lineName === line.lineName ? { ...row, mode: e.target.value } : row)))}
                      sx={{ width: 130 }}
                    >
                      <MenuItem value="percent">Percent</MenuItem>
                      <MenuItem value="rupees">Fixed amount</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label={line.mode === 'rupees' ? 'Rupees' : 'Percent'}
                      type="number"
                      value={line.mode === 'rupees' ? line.fixedAmount : line.percentage}
                      onChange={(e) => setLines(draft.lines.map((row) => (row.lineName === line.lineName
                        ? { ...row, [line.mode === 'rupees' ? 'fixedAmount' : 'percentage']: e.target.value }
                        : row)))}
                      sx={{ width: 120 }}
                    />
                  </>
                )}
                {!line.takesRest && exampleReady && <Typography sx={{ minWidth: 90, textAlign: 'right' }}>{rupee(share)}</Typography>}
                {!line.takesRest && <Button size="small" onClick={() => makeRest(line.lineName)}>Put the rest here</Button>}
                <Button size="small" onClick={() => setLines(draft.lines.filter((row) => row.lineName !== line.lineName))} disabled={lines.length === 1}>Remove</Button>
              </Stack>
            );
          })}
        </Stack>
        {catalog.some((name) => !chosen.has(name)) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {catalog.filter((name) => !chosen.has(name)).map((name) => (
              <Chip key={name} label={`Add ${name}`} variant="outlined" onClick={() => addLine(name)} />
            ))}
          </Stack>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <TextField size="small" label="Add a different part" value={customName} onChange={(e) => setCustomName(e.target.value)} fullWidth />
          <Button onClick={() => { addLine(customName.trim()); setCustomName(''); }}>Add</Button>
        </Stack>
        {restOnExample != null && restOnExample < 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>On this salary the other parts are already too much. Lower a percent or a fixed amount.</Alert>
        )}
        {percentSum > 100.001 && (
          <Alert severity="warning" sx={{ mt: 2 }}>The percents are already over 100%. Lower one of them.</Alert>
        )}
        {basic && basic.mode !== 'rupees' && basic.percentage !== '' && Number(basic.percentage) < 50 && (
          <Alert severity="info" sx={{ mt: 2 }}>Basic pay is under 50%. You can still save. The suggested start is 50%.</Alert>
        )}
        {exampleReady && excludedOnExample > example / 2 && (
          <Alert severity="info" sx={{ mt: 2 }}>House rent and conveyance are over half of this salary. The extra is counted as wages. The split is saved as you set it.</Alert>
        )}
      </Section>
    </Box>
  );
}

function Incentives({ categories, rates, onSaveRate, onSaveCategory }) {
  const [rate, setRate] = useState({ code: 'demo', amount: '', effectiveFrom: '' });
  const [category, setCategory] = useState({ code: '', name: '' });
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Section title="Demo, trial, and retainer" hint="The rupee amount paid per completed demo or trial, and the retainer amount when that count is decided.">
        <Stack spacing={2}>
          <TextField select label="Which amount" value={rate.code} onChange={(e) => setRate({ ...rate, code: e.target.value })} fullWidth>
            <MenuItem value="demo">Demo</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="yr_retainer">1YrRetainer</MenuItem>
          </TextField>
          <TextField label="Rupees" type="number" value={rate.amount} onChange={(e) => setRate({ ...rate, amount: e.target.value })} fullWidth />
          <TextField label="Effective from" value={rate.effectiveFrom} onChange={(e) => setRate({ ...rate, effectiveFrom: e.target.value })} {...dateField} />
          <Button variant="contained" onClick={() => onSaveRate({ ...rate, amount: Number(rate.amount) })}>Save amount</Button>
          {rates.map((row) => (
            <Typography key={row.id} variant="body2">{row.code} · ₹{row.amount} from {String(row.effective_from).slice(0, 10)}</Typography>
          ))}
        </Stack>
      </Section>
      <Section title="Other incentive" hint="A named monthly amount, such as a management incentive. It is not Demo, Trial, or 1YrRetainer.">
        <Stack spacing={2}>
          <TextField label="Code" value={category.code} onChange={(e) => setCategory({ ...category, code: e.target.value })} fullWidth />
          <TextField label="Name people will see" value={category.name} onChange={(e) => setCategory({ ...category, name: e.target.value })} fullWidth />
          <Button variant="contained" onClick={() => onSaveCategory(category)}>Save category</Button>
          {categories.map((row) => (
            <Stack key={row.id} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ flex: 1 }}>{row.name}{row.is_active ? '' : ' · inactive'}</Typography>
              {row.is_active && !['demo', 'trial', 'yr_retainer'].includes(row.code) && (
                <Button size="small" onClick={() => onSaveCategory({ id: row.id, name: row.name, isActive: false })}>Stop using</Button>
              )}
            </Stack>
          ))}
        </Stack>
      </Section>
    </Box>
  );
}

function flagText(value) {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
}

function YesNo({ label, value, onChange }) {
  return (
    <TextField select label={label} value={value} onChange={onChange} fullWidth>
      <MenuItem value="">Not known yet</MenuItem>
      <MenuItem value="yes">Yes</MenuItem>
      <MenuItem value="no">No</MenuItem>
    </TextField>
  );
}

function EmployerAndBasis({ statutes, employerRow, onChanged }) {
  const [employer, setEmployer] = useState({
    legalName: '', pan: '', tan: '', registeredAddress: '',
    pfRegistered: '', esiRegistered: '', lwfRegistered: '', shopsRegistered: '', tanRegistered: '',
  });
  const [basis, setBasis] = useState({ statuteCode: 'pf', jurisdictionBasis: 'central', basisSource: '' });
  useEffect(() => {
    if (!employerRow) return;
    setEmployer({
      legalName: employerRow.legal_name || '',
      pan: employerRow.pan || '',
      tan: employerRow.tan || '',
      registeredAddress: employerRow.registered_address || '',
      pfRegistered: flagText(employerRow.pf_registered),
      esiRegistered: flagText(employerRow.esi_registered),
      lwfRegistered: flagText(employerRow.lwf_registered),
      shopsRegistered: flagText(employerRow.shops_registered),
      tanRegistered: flagText(employerRow.tan_registered),
    });
  }, [employerRow]);
  const flag = (value) => (value === 'yes' ? true : value === 'no' ? false : null);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Section title="Employer" hint="Bacchapanti, the Haryana proprietorship. Registration answers stay blank until you know them.">
        <Stack spacing={2}>
          <TextField label="Trade name" value={employer.legalName} onChange={(e) => setEmployer({ ...employer, legalName: e.target.value })} fullWidth />
          <TextField label="PAN" value={employer.pan} onChange={(e) => setEmployer({ ...employer, pan: e.target.value })} fullWidth />
          <TextField label="TAN" value={employer.tan} onChange={(e) => setEmployer({ ...employer, tan: e.target.value })} fullWidth />
          <TextField label="Registered address" value={employer.registeredAddress} onChange={(e) => setEmployer({ ...employer, registeredAddress: e.target.value })} fullWidth />
          <YesNo label="PF registered" value={employer.pfRegistered} onChange={(e) => setEmployer({ ...employer, pfRegistered: e.target.value })} />
          <YesNo label="ESI registered" value={employer.esiRegistered} onChange={(e) => setEmployer({ ...employer, esiRegistered: e.target.value })} />
          <YesNo label="Labour welfare fund registered" value={employer.lwfRegistered} onChange={(e) => setEmployer({ ...employer, lwfRegistered: e.target.value })} />
          <YesNo label="Shops registered" value={employer.shopsRegistered} onChange={(e) => setEmployer({ ...employer, shopsRegistered: e.target.value })} />
          <YesNo label="TAN registered" value={employer.tanRegistered} onChange={(e) => setEmployer({ ...employer, tanRegistered: e.target.value })} />
          <Button variant="contained" onClick={() => payrollAPI.saveEmployer({
            ...employer,
            pfRegistered: flag(employer.pfRegistered),
            esiRegistered: flag(employer.esiRegistered),
            lwfRegistered: flag(employer.lwfRegistered),
            shopsRegistered: flag(employer.shopsRegistered),
            tanRegistered: flag(employer.tanRegistered),
          }).then(() => { toast.success('Employer saved'); onChanged(); }).catch((e) => toast.error(e.response?.data?.message || 'Request failed'))}>Save employer</Button>
        </Stack>
      </Section>
      <Section title="Which place each rule follows" hint="Already set. Change one only when the chartered accountant says the place is different.">
        <Stack spacing={2}>
          <TextField select label="Which rule" value={basis.statuteCode} onChange={(e) => setBasis({ ...basis, statuteCode: e.target.value })} fullWidth>
            {statutes.map((s) => <MenuItem key={s.code} value={s.code}>{statuteLabel(s.code)}</MenuItem>)}
          </TextField>
          <TextField select label="Follows" value={basis.jurisdictionBasis} onChange={(e) => setBasis({ ...basis, jurisdictionBasis: e.target.value })} fullWidth>
            <MenuItem value="central">Central rule</MenuItem>
            <MenuItem value="establishment_state">Establishment state</MenuItem>
            <MenuItem value="posting_state">Place of posting</MenuItem>
            <MenuItem value="actual_work_state">Approved work state</MenuItem>
            <MenuItem value="manual_only">Typed for each person</MenuItem>
          </TextField>
          <TextField label="Why this is the right fact" value={basis.basisSource} onChange={(e) => setBasis({ ...basis, basisSource: e.target.value })} fullWidth />
          <Button variant="contained" onClick={() => payrollAPI.confirmBasis(basis).then(() => { toast.success('Saved'); onChanged(); }).catch((e) => toast.error(e.response?.data?.message || 'Request failed'))}>Save</Button>
        </Stack>
      </Section>
    </Box>
  );
}

function Statutory({ rules, reviews, onSaveRule, onSaveReview }) {
  const [rule, setRule] = useState({
    statuteCode: 'pf',
    employeeCategory: 'employee',
    employeeRate: '',
    employerRate: '',
    employeeContributionBase: 'wages',
    employerContributionBase: 'wages',
    effectiveFrom: '',
    sourceTitle: '',
    notificationNumber: '',
    verificationStatus: 'verified',
  });
  return (
    <Stack spacing={2}>
      <Section title="Figures in use" hint="Payroll uses these. Replace a figure only after the chartered accountant corrects the review note.">
        <Stack spacing={0.5}>
          {rules.map((row) => (
            <Typography key={`${row.statute_code}-${row.notification_number}`} variant="body2">
              {statuteLabel(row.statute_code)}{row.state_code ? ` · ${row.state_code}` : ''} · from the person {row.employee_rate != null ? `${row.employee_rate}%` : 'a formula'} · from Bacchapanti {row.employer_rate != null ? `${row.employer_rate}%` : 'a formula'} · from {String(row.effective_from).slice(0, 10)}
            </Typography>
          ))}
          {rules.length === 0 && <Typography variant="body2">No figures are loaded yet. Run the payroll SQL, then refresh.</Typography>}
        </Stack>
      </Section>
      <Section title="Replace a figure" hint="A verified figure needs the notification number and the source title.">
        <FieldGrid>
          <TextField select label="Which rule" value={rule.statuteCode} onChange={(e) => setRule({ ...rule, statuteCode: e.target.value })} fullWidth>
            {['pf', 'esi', 'pt', 'lwf', 'tds_salary', 'tds_professional_fee', 'gratuity', 'bonus'].map((code) => <MenuItem key={code} value={code}>{statuteLabel(code)}</MenuItem>)}
          </TextField>
          <TextField select label="Applies to" value={rule.employeeCategory} onChange={(e) => setRule({ ...rule, employeeCategory: e.target.value })} fullWidth>
            <MenuItem value="employee">Employee</MenuItem>
            <MenuItem value="contractor">Contractor</MenuItem>
          </TextField>
          <TextField label="Percent from the person" value={rule.employeeRate} onChange={(e) => setRule({ ...rule, employeeRate: e.target.value })} fullWidth />
          <TextField label="Percent from Bacchapanti" value={rule.employerRate} onChange={(e) => setRule({ ...rule, employerRate: e.target.value })} fullWidth />
          <TextField select label="Person’s percent is of" value={rule.employeeContributionBase} onChange={(e) => setRule({ ...rule, employeeContributionBase: e.target.value })} fullWidth>
            <MenuItem value="wages">Wages</MenuItem>
            <MenuItem value="gross">Full monthly pay</MenuItem>
          </TextField>
          <TextField select label="Bacchapanti’s percent is of" value={rule.employerContributionBase} onChange={(e) => setRule({ ...rule, employerContributionBase: e.target.value })} fullWidth>
            <MenuItem value="wages">Wages</MenuItem>
            <MenuItem value="gross">Full monthly pay</MenuItem>
          </TextField>
          <TextField label="Effective from" value={rule.effectiveFrom} onChange={(e) => setRule({ ...rule, effectiveFrom: e.target.value })} {...dateField} />
          <TextField label="Notification number" value={rule.notificationNumber} onChange={(e) => setRule({ ...rule, notificationNumber: e.target.value })} fullWidth />
          <TextField label="Source title" value={rule.sourceTitle} onChange={(e) => setRule({ ...rule, sourceTitle: e.target.value })} fullWidth />
        </FieldGrid>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => onSaveRule({
          ...rule,
          employeeRate: rule.employeeRate === '' ? null : Number(rule.employeeRate),
          employerRate: rule.employerRate === '' ? null : Number(rule.employerRate),
          employeeContributionBase: rule.employeeContributionBase || null,
          employerContributionBase: rule.employerContributionBase || null,
        })}>Save replacement</Button>
      </Section>
      <Section title="Next review" hint="A date here only reminds you. It does not change a figure.">
        {reviews.map((row) => <ReviewRow key={row.item_key} row={row} onSave={(nextReviewOn) => onSaveReview(row.item_key, nextReviewOn)} />)}
      </Section>
    </Stack>
  );
}

function ReviewRow({ row, onSave }) {
  const [date, setDate] = useState(row.next_review_on ? String(row.next_review_on).slice(0, 10) : '');
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ flex: 1 }}>{row.item_label}</Typography>
      <TextField size="small" type="date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
      <Button size="small" onClick={() => onSave(date)}>Save date</Button>
    </Stack>
  );
}
